/**
 * Per-user navigation layout — order and visibility of the sidebar items.
 *
 * Stored on `user_profiles.nav_preferences` (JSONB) so a user's layout follows
 * them between machines, with a localStorage mirror so the sidebar renders in
 * the user's own order immediately on load and still works if the write fails.
 *
 * Requires migration 019. If that column does not exist yet the remote save is
 * skipped and the local copy is still honoured, so the feature degrades to
 * browser-local rather than breaking the sidebar.
 */

import { supabase } from '../lib/supabase'

export interface NavPreferences {
  /** Nav item ids in the user's chosen order. Unknown ids are ignored. */
  order: string[]
  /** Nav item ids the user has chosen to hide. */
  hidden: string[]
  /** Bumped whenever the shape changes so stale local copies can be discarded. */
  version: number
}

/**
 * 2 — Winnie, 13 Sep: Cost of Sales moved into Finance & Insights and Standard Costs
 * into Admin. A saved order holds ids only, so an order saved against v1 would pin both
 * items to their old slots and, because grouping is positional, suppress every section
 * heading for that user. Bumping the version drops those saved orders rather than
 * leaving a handful of people looking at a nav that quietly refused to move.
 */
export const NAV_PREFS_VERSION = 2

export const emptyNavPreferences = (): NavPreferences => ({
  order: [],
  hidden: [],
  version: NAV_PREFS_VERSION,
})

const localKey = (userId: string) => `directauto.nav.${userId}`

const sanitise = (raw: any): NavPreferences => {
  if (!raw || typeof raw !== 'object') return emptyNavPreferences()
  const order = Array.isArray(raw.order) ? raw.order.filter((x: any) => typeof x === 'string') : []
  const hidden = Array.isArray(raw.hidden) ? raw.hidden.filter((x: any) => typeof x === 'string') : []
  return { order, hidden, version: NAV_PREFS_VERSION }
}

export const navPreferencesService = {
  /** Read the local copy synchronously so the first paint is already correct. */
  readLocal(userId: string): NavPreferences {
    try {
      const raw = localStorage.getItem(localKey(userId))
      if (!raw) return emptyNavPreferences()
      const parsed = JSON.parse(raw)
      if (parsed?.version !== NAV_PREFS_VERSION) return emptyNavPreferences()
      return sanitise(parsed)
    } catch {
      return emptyNavPreferences()
    }
  },

  writeLocal(userId: string, prefs: NavPreferences) {
    try {
      localStorage.setItem(localKey(userId), JSON.stringify(prefs))
    } catch {
      /* private browsing / quota — the remote copy is still authoritative */
    }
  },

  /** Fetch the stored layout. Returns null when there is nothing saved. */
  async load(userId: string): Promise<NavPreferences | null> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('nav_preferences')
        .eq('id', userId)
        .maybeSingle()

      if (error) return null
      if (!data?.nav_preferences) return null

      // A layout saved against an older version was ordered against an older nav. Drop
      // it and write the empty layout back, so the canonical order applies everywhere
      // rather than only on devices whose local copy happened to be cleared.
      const storedVersion = (data.nav_preferences as any)?.version
      if (storedVersion !== NAV_PREFS_VERSION) {
        const fresh = emptyNavPreferences()
        this.writeLocal(userId, fresh)
        void supabase.from('user_profiles').update({ nav_preferences: fresh }).eq('id', userId)
        return fresh
      }

      const prefs = sanitise(data.nav_preferences)
      this.writeLocal(userId, prefs)
      return prefs
    } catch {
      return null
    }
  },

  /**
   * Persist the layout. Always writes locally first so the UI never appears to
   * lose the change, then mirrors to the profile. Returns false when only the
   * local copy could be saved.
   */
  async save(userId: string, prefs: NavPreferences): Promise<boolean> {
    const clean = sanitise(prefs)
    this.writeLocal(userId, clean)
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ nav_preferences: clean })
        .eq('id', userId)
      return !error
    } catch {
      return false
    }
  },

  async reset(userId: string): Promise<boolean> {
    return this.save(userId, emptyNavPreferences())
  },
}

/**
 * Apply a saved layout to the canonical item list.
 *
 * Items the user has never seen (new features shipped since they last saved)
 * keep their position from the canonical list rather than being dumped at the
 * end, so a new screen appears where the designer put it.
 */
export function applyNavOrder<T extends { id: string }>(items: T[], order: string[]): T[] {
  if (!order.length) return items

  const rank = new Map(order.map((id, i) => [id, i]))
  const known = items.filter((i) => rank.has(i.id))
  const unknown = items.filter((i) => !rank.has(i.id))

  known.sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0))

  // Re-insert never-seen items at their canonical index.
  const result = [...known]
  for (const item of unknown) {
    const canonicalIndex = items.indexOf(item)
    result.splice(Math.min(canonicalIndex, result.length), 0, item)
  }
  return result
}
